<template>
  <v-dialog
    :value="true"
    scrollable
    width="500"
    @click:outside="handleClose"
  >
    <v-card :loading="isProcessing || isLoading">
      <v-card-title>
        <span>Update {{ bucketName }} A/B Test Evaluation order</span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div
        v-if="isLoading"
        class="d-flex flex-column align-center py-4 px-4"
      >
        <h3 class="my-12">
          Please wait while we load active a/b tests...
        </h3>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>

      <v-card-text v-else>
        <v-list dense>
          <v-list-item-group>
            <draggable
              class="list-group"
              ghost-class="ghost"
              :list="activeAbTests"
              :disabled="isProcessing"
              group="ab-campaign-elements"
            >
              <v-list-item
                v-for="(item, index) in activeAbTests"
                :key="index"
              >
                <v-list-item-content>
                  <div class="d-flex align-center justify-space-between">
                    <span>
                      <v-chip
                        label
                        color="blue"
                        text-color="white"
                        class="mr-2"
                      >
                        {{ index + 1 }}
                      </v-chip>

                      <span class="font-weight-bold">
                        {{ item.test_name }}
                      </span>
                    </span>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </draggable>
          </v-list-item-group>
        </v-list>
      </v-card-text>

      <v-divider />

      <v-card-actions class="py-4">
        <v-spacer />

        <v-btn
          class="btn-bg-deep-blue"
          text-color="white"
          @click="handleSave"
        >
          Save changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import draggable from 'vuedraggable';
import { mapActions } from 'vuex';
</script>

<script>
export default {
  name: 'AbTestEvaluationOrderModal',

  props: {
    bucketId: {
      type: Number,
      required: true,
    },
    bucketName: {
      type: String,
      default: '',
    },
  },

  emits: ['close', 'priorities-updated'],

  data() {
    return {
      isLoading: true,
      isProcessing: false,
      activeAbTests: [],
    };
  },

  watch: {
    bucketId: {
      immediate: true,
      handler(newVal) {
        this.fetchActiveCampaigns(newVal);
      },
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    handleClose() {
      this.$emit('close');
    },

    async fetchActiveCampaigns(bucketId) {
      try {
        this.isLoading = true;

        const { data } = await this.$axios.get('/api/v1/ab-tests/active', {
          params: {
            bucket_id: bucketId,
          },
        });

        this.activeAbTests = data?.data || [];
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Failed to fetch active a/b tests',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleSave() {
      this.isProcessing = true;

      try {
        const activeAbTests = this.activeAbTests.map((item, index) => ({
          id: item.id,
          evaluation_order: index + 1,
        }));

        await this.$axios.put('/api/v1/ab-tests/evaluation-order', {
          bucket_id: this.bucketId,
          activeAbTests,
        });

        this.setNotification({
          color: 'green',
          message: 'A/B Test priorities updated successfully',
        });
        this.$emit('updated');
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Failed to update A/B Test priorities',
        });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.list-group > div {
  cursor: move;
  border: 1px solid #e0e0e0;
  margin-bottom: 5px;
}
</style>
